import { REVIEW_REMOVE } from "@rosc/ui/v32/review-card";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Pencil,
  Trash2,
  X,
} from "lucide-react";
import type { JSX } from "react";
import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { cn } from "@/lib/utils";
import {
  applyVerifyAction,
  extractVerifyFlags,
  isPreChartFlag,
  type VerifyAction,
} from "@/lib/verifyFlags";

// v3.2 review card for "[verify]" values (VITE_REVIEW_CARD_ENABLED), the web
// port of the iOS ReviewCard: the flagged value in its line, Accept (the
// marker goes, the value stays), Edit (the value is replaced and the
// marker goes) and Remove (the value goes from the note; an Edit saved
// empty does the same), one value at a time with previous / next. Each change is
// saved through the note's normal save, so it lands in the version history.
// Desktop: a popover under the highlighted value. Phones: a bottom sheet.
//
// No "Hear this moment" on web yet: visit transcripts have no timestamps, so
// there is no reliable way to find where a value was said.

const CARD_WIDTH = 384;
const GAP = 10;
const MARGIN = 16;
// Room kept under a value for the card before it has measured itself.
const CARD_ROOM = 320;

interface Props {
  note: string;
  // The flag shown (0-based marker index in the note).
  index: number;
  // Holds the highlighted values ([data-verify-index]), for placement.
  container: HTMLElement | null;
  phone: boolean;
  // Saves the note; resolves true when saved. Without it, read only.
  // `action`: how the value was resolved ("remove" offers Undo).
  onSave?: (next: string, action: VerifyAction["type"]) => Promise<boolean>;
  onIndexChange: (index: number) => void;
  onClose: () => void;
  // "Accept all" (VITE_REVIEW_COPY_ANYWAY_ENABLED): asks to accept every
  // open value at once. Shown when more than one value is left.
  onAcceptAll?: () => void;
  // Pre-chart visits: why their values are marked (a pre-chart value the
  // visit changed, or a dictated name to confirm).
  wording?: { title: string; explain: string };
}

export const findVerifyHighlight = (
  container: HTMLElement | null,
  index: number,
): HTMLElement | null =>
  container?.querySelector<HTMLElement>(`[data-verify-index="${index}"]`) ??
  null;

export const ReviewCard = ({
  note,
  index,
  container,
  phone,
  onSave,
  onIndexChange,
  onClose,
  onAcceptAll,
  wording,
}: Props): JSX.Element | null => {
  const { t } = useTranslation();
  const titleId = useId();
  const cardRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const flags = extractVerifyFlags(note);
  const flag = flags[index];
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState<VerifyAction["type"] | null>(null);
  const [position, setPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  // A new value starts in the read state.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset per flag shown.
  useEffect(() => {
    setEditing(false);
    setBusy(null);
  }, [index, flag?.rawValue]);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  // Mark the highlight the card is about, and bring it into view. The note
  // re-renders its highlights after each save, so this runs again then.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `note` re-runs it on the new highlight.
  useEffect(() => {
    const anchor = findVerifyHighlight(container, index);
    if (!anchor) return;
    anchor.setAttribute("data-active", "true");
    anchor.style.scrollMarginTop = phone ? "88px" : "";
    // Desktop: leave room under the value for the card, so it opens below
    // it instead of over the Transcript bar and the review banner.
    anchor.style.scrollMarginBottom = phone
      ? ""
      : `${(cardRef.current?.offsetHeight ?? CARD_ROOM) + GAP + MARGIN}px`;
    anchor.scrollIntoView?.({
      block: phone ? "start" : "nearest",
      behavior: "smooth",
    });
    return () => anchor.removeAttribute("data-active");
  }, [container, index, phone, note]);

  // Desktop: under the value (above it when there is no room), kept on
  // screen while the note scrolls.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `note` and `editing` change the anchor and the card height.
  useLayoutEffect(() => {
    if (phone) return;
    let frame = 0;
    const place = (): void => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const anchor = findVerifyHighlight(container, index);
        const card = cardRef.current;
        if (!anchor || !card) return;
        const rect = anchor.getBoundingClientRect();
        const width = Math.min(CARD_WIDTH, window.innerWidth - 2 * MARGIN);
        const height = card.offsetHeight;
        const left = Math.min(
          Math.max(MARGIN, rect.left - 12),
          window.innerWidth - width - MARGIN,
        );
        const below = rect.bottom + GAP;
        const top =
          below + height <= window.innerHeight - MARGIN ||
          rect.top - GAP - height < MARGIN
            ? below
            : rect.top - GAP - height;
        setPosition((current) =>
          current?.top === top && current.left === left
            ? current
            : { top, left },
        );
      });
    };
    place();
    window.addEventListener("scroll", place, { capture: true, passive: true });
    window.addEventListener("resize", place);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", place, { capture: true });
      window.removeEventListener("resize", place);
    };
  }, [container, index, phone, note, editing]);

  // A press outside the card and the highlights closes it (desktop; the
  // phone sheet has its own close button).
  useEffect(() => {
    if (phone) return;
    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target;
      if (!(target instanceof Element) || busy) return;
      if (cardRef.current?.contains(target)) return;
      if (target.closest("[data-verify-index], [data-testid=needs-review]"))
        return;
      onClose();
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [busy, onClose, phone]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape" && !busy) {
        if (editing) setEditing(false);
        else onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, editing, onClose]);

  if (!flag) return null;

  const total = flags.length;
  // A pre-chart item the visit never mentioned: its own reason (Remove when
  // the patient no longer has or takes it).
  const notMentioned = isPreChartFlag(flag);
  const title = notMentioned
    ? t("preChart.card.notMentionedTitle")
    : (wording?.title ?? t("visit.workspace.verify.card.title"));
  const explain = notMentioned
    ? t("preChart.card.notMentionedExplain")
    : (wording?.explain ?? t("visit.workspace.verify.card.explain"));
  const resolve = async (action: VerifyAction): Promise<void> => {
    if (!onSave) return;
    // An Edit saved empty removes the value.
    const type =
      action.type === "edit" && !action.value.trim() ? "remove" : action.type;
    const next = applyVerifyAction(note, index, action);
    if (next === note) return;
    setBusy(type);
    let saved = false;
    try {
      saved = await onSave(next, type);
    } catch {
      saved = false;
    }
    // A failed save keeps the value flagged; the save shows its own error.
    setBusy(null);
    if (saved) setEditing(false);
  };

  const iconButton =
    "inline-flex size-11 items-center justify-center rounded-full text-v3-text-2 transition-colors hover:bg-v3-surface-2 hover:text-v3-text focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-v3-brand-ring disabled:pointer-events-none disabled:opacity-40 lg:size-8";
  const primary =
    "inline-flex h-11 items-center justify-center gap-2 rounded-full bg-v3-ink px-5 text-[14px] font-semibold text-v3-on-ink transition-opacity hover:opacity-90 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-v3-brand-ring focus-visible:ring-offset-2 focus-visible:ring-offset-v3-panel disabled:pointer-events-none disabled:opacity-50 lg:h-10";
  const quiet =
    "inline-flex h-11 items-center justify-center gap-2 rounded-full px-4 text-[14px] font-semibold text-v3-text transition-colors hover:bg-v3-surface-2 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-v3-brand-ring disabled:pointer-events-none disabled:opacity-50 lg:h-10";

  const header = (
    <div className="flex items-center gap-1">
      <h2
        className="flex min-w-0 flex-1 items-center gap-2 text-[15px] font-semibold text-v3-warn-text dark:text-v3-warn"
        id={titleId}
      >
        <span aria-hidden className="size-2 shrink-0 rounded-full bg-v3-warn" />
        {title}
      </h2>
      {total > 1 && (
        <>
          <button
            aria-label={t("visit.workspace.verify.card.previous")}
            className={iconButton}
            disabled={!!busy}
            type="button"
            onClick={() => onIndexChange((index - 1 + total) % total)}
          >
            <ChevronLeft aria-hidden className="size-4" />
          </button>
          <span
            aria-live="polite"
            className="min-w-12 text-center font-mono text-[13px] tabular-nums text-v3-text-2"
            data-testid="review-card-position"
          >
            {t("visit.workspace.verify.card.position", {
              current: index + 1,
              total,
            })}
          </span>
          <button
            aria-label={t("visit.workspace.verify.card.next")}
            className={iconButton}
            disabled={!!busy}
            type="button"
            onClick={() => onIndexChange((index + 1) % total)}
          >
            <ChevronRight aria-hidden className="size-4" />
          </button>
        </>
      )}
      <button
        aria-label={t("visit.workspace.verify.card.close")}
        className={iconButton}
        disabled={!!busy}
        type="button"
        onClick={onClose}
      >
        <X aria-hidden className="size-4" />
      </button>
    </div>
  );

  const progress =
    total > 1 ? (
      <div aria-hidden className="mt-2 flex gap-1">
        {flags.map((item) => (
          <span
            key={item.index}
            className={cn(
              "h-1 flex-1 rounded-full",
              item.index === index ? "bg-v3-warn" : "bg-v3-surface-3",
            )}
          />
        ))}
      </div>
    ) : null;

  const line = (
    <p
      className="mt-3 rounded-xl bg-v3-warn-tint px-3.5 py-3 text-[16px] leading-relaxed text-v3-text shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--v3-warn)_35%,transparent)] wrap-break-word"
      data-testid="review-card-line"
    >
      {flag.before && <span className="text-v3-text-2">{flag.before}</span>}
      <strong className="font-semibold">{flag.value}</strong>
      {flag.after && <span className="text-v3-text-2">{flag.after}</span>}
    </p>
  );

  const actions = !onSave ? (
    <p className="mt-4 text-sm text-v3-text-2">
      {t("visit.workspace.verify.card.readOnly")}
    </p>
  ) : editing ? (
    <form
      className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center"
      onSubmit={(event) => {
        event.preventDefault();
        void resolve({ type: "edit", value });
      }}
    >
      <label className="sr-only" htmlFor={`${titleId}-value`}>
        {t("visit.workspace.verify.card.editLabel")}
      </label>
      <input
        ref={inputRef}
        className="h-11 w-full min-w-0 shrink-0 rounded-[10px] border-0 bg-v3-surface-2 px-3 text-[16px] text-v3-text shadow-v3-hairline outline-hidden placeholder:text-v3-text-3 focus:ring-2 focus:ring-v3-brand-ring sm:w-auto sm:flex-1 lg:h-10 lg:text-[15px]"
        data-testid="review-card-input"
        disabled={!!busy}
        id={`${titleId}-value`}
        placeholder={t("visit.workspace.verify.card.emptyHint")}
        value={value}
        onChange={(event) => setValue(event.target.value)}
      />
      <div className="flex gap-2">
        <button
          className={cn(primary, "flex-1 sm:flex-none")}
          data-empty={value.trim() ? undefined : "true"}
          data-testid="review-card-save"
          disabled={!!busy}
          type="submit"
        >
          {busy === "edit" || busy === "remove" ? <LoadingSpinner /> : null}
          {value.trim()
            ? t("visit.workspace.verify.card.save")
            : t("visit.workspace.verify.card.remove")}
        </button>
        <button
          className={cn(quiet, "flex-1 sm:flex-none")}
          disabled={!!busy}
          type="button"
          onClick={() => setEditing(false)}
        >
          {t("visit.workspace.verify.card.cancel")}
        </button>
      </div>
    </form>
  ) : (
    // Wraps when Remove leaves no room for Accept all.
    <div className="mt-4 flex flex-wrap items-center gap-x-2 gap-y-1">
      <button
        aria-label={t("visit.workspace.verify.card.acceptAria", {
          value: flag.value,
        })}
        className={primary}
        data-testid="review-card-accept"
        disabled={!!busy}
        type="button"
        onClick={() => void resolve({ type: "accept" })}
      >
        {busy === "accept" ? (
          <LoadingSpinner />
        ) : (
          <Check aria-hidden className="size-4" />
        )}
        {t("visit.workspace.verify.card.accept")}
      </button>
      <button
        aria-label={t("visit.workspace.verify.card.editAria", {
          value: flag.value,
        })}
        className={quiet}
        data-testid="review-card-edit"
        disabled={!!busy}
        type="button"
        onClick={() => {
          setValue(flag.value);
          setEditing(true);
        }}
      >
        <Pencil aria-hidden className="size-4" />
        {t("visit.workspace.verify.card.edit")}
      </button>
      <button
        aria-label={t("visit.workspace.verify.card.removeAria", {
          value: flag.value,
        })}
        className={REVIEW_REMOVE}
        data-testid="review-card-remove"
        disabled={!!busy}
        type="button"
        onClick={() => void resolve({ type: "remove" })}
      >
        {busy === "remove" ? (
          <LoadingSpinner />
        ) : (
          <Trash2 aria-hidden className="size-4" />
        )}
        {notMentioned
          ? t("preChart.card.remove")
          : t("visit.workspace.verify.card.remove")}
      </button>
      {onAcceptAll && total > 1 && (
        <button
          className={cn(quiet, "ml-auto px-3 text-v3-text-2")}
          data-testid="review-card-accept-all"
          disabled={!!busy}
          type="button"
          onClick={onAcceptAll}
        >
          {t("visit.workspace.verify.bulk.acceptAllCount", { count: total })}
        </button>
      )}
    </div>
  );

  const content = (
    <>
      {header}
      {progress}
      {line}
      <p className="mt-3 border-l-2 border-v3-border-strong pl-3 text-[13.5px] leading-relaxed text-v3-text-2">
        {explain}
      </p>
      {actions}
    </>
  );

  if (phone)
    return (
      <div
        ref={cardRef}
        aria-labelledby={titleId}
        className="fixed inset-x-0 bottom-0 z-50 max-h-[75dvh] overflow-y-auto rounded-t-[22px] bg-v3-panel px-4 pt-2 pb-[max(1rem,env(safe-area-inset-bottom))] font-v3 text-v3-text shadow-v3-float animate-in slide-in-from-bottom-8 fade-in-0 duration-200"
        data-testid="review-card"
        role="dialog"
      >
        <div
          aria-hidden
          className="mx-auto mb-2 h-1 w-10 rounded-full bg-v3-surface-3"
        />
        {content}
      </div>
    );

  return (
    <div
      ref={cardRef}
      aria-labelledby={titleId}
      className={cn(
        "fixed z-50 rounded-[18px] bg-v3-panel p-4 font-v3 text-v3-text shadow-v3-pop animate-in fade-in-0 zoom-in-95 duration-150",
        !position && "invisible",
      )}
      data-testid="review-card"
      role="dialog"
      style={{
        top: position?.top ?? 0,
        left: position?.left ?? 0,
        width: `min(${CARD_WIDTH}px, calc(100vw - ${2 * MARGIN}px))`,
      }}
    >
      {content}
    </div>
  );
};
